import { put, call, takeLatest, select, getContext, fork } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios, { CancelToken } from 'axios';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { getActiveRecruitersByLoggedInUserId } from 'app/services/CandidateServices/CandidateServices';
import { httpSuccess } from 'app/services/serviceHelpers';
import { trackPromise } from 'react-promise-tracker';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { selectUser } from 'oidc/user.selectors';
import { Concatenate } from 'utils/string/string';
import { myPerformanceActions, WIDGET_KEYS, type WidgetKey, type IRecruiterOption } from './MyPerformance.redux';

const TRACK_AREA = 'MyPerformance-dashboard';

let myPerformanceCancelSource: ReturnType<typeof axios.CancelToken.source> | null = null;
const Track = (fn: () => Promise<any>) => trackPromise(fn(), TRACK_AREA);
const TrackRecruiters = (fn: (...args: any[]) => Promise<any>, ...args: any[]) =>
  trackPromise(fn(...args), 'MyPerformance-recruiter-options');

function buildRecruiterOptions(
  recruiters: any[],
  userInfo: { employeeId: number; firstName?: string; lastName?: string },
): IRecruiterOption[] {
  const currentUser = {
    id: userInfo.employeeId,
    name: Concatenate([userInfo.firstName ?? '', userInfo.lastName ?? ''], ' '),
    value: userInfo.employeeId,
    object: { userId: userInfo.employeeId, firstName: userInfo.firstName, lastName: userInfo.lastName },
  };
  const others = (recruiters ?? [])
    .filter((r: any) => r.userId !== userInfo.employeeId)
    .map((r: any) => ({
      id: r.userId,
      name: Concatenate([r.firstName ?? '', r.lastName ?? ''], ' '),
      value: r.userId,
      object: { userId: r.userId, firstName: r.firstName, lastName: r.lastName },
    }));
  return [currentUser, ...others];
}

function getResponseData(res: any): any {
  if (!res) return null;
  if (Array.isArray(res)) return res;
  return res?.data ?? res?.Data ?? res;
}

function* fetchWidget(
  placementService: PlacementService,
  widget: WidgetKey,
  params: { employeeId: number; startDate: string; endDate: string; daysBack: number; cancelToken?: CancelToken },
) {
  try {
    const res = yield call(Track, () =>
      placementService.getMyPerformanceDashboard(
        {
          employeeId: params.employeeId,
          widget,
          startDate: params.startDate,
          endDate: params.endDate,
          daysBack: params.daysBack,
        },
        params.cancelToken ? { cancelToken: params.cancelToken } : undefined,
      ),
    );
    const response = httpSuccess(res?.status) ? getResponseData(res) : null;
    yield put(myPerformanceActions.setWidgetData({ widget, response: response ?? [] }));
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchWidget',
        area: 'src/app/components/RecruiterWorkDesk/MyPerformanceDesk/MyPerformance.saga.ts',
        widget,
      },
    });
    yield put(myPerformanceActions.setWidgetLoadError(widget));
  }
}

export function* fetchMyPerformanceDashboard(
  action: PayloadAction<{ employeeId: number; startDate?: string; endDate?: string; daysBack?: number }>,
) {
  if (myPerformanceCancelSource) {
    myPerformanceCancelSource.cancel('Request cancelled - filter or reset changed');
  }
  myPerformanceCancelSource = axios.CancelToken.source();
  const placementService: PlacementService = yield getContext('placementService');
  if (!placementService) {
    yield put(myPerformanceActions.setLoadError(true));
    return;
  }

  yield put(myPerformanceActions.resetFetch());

  const { employeeId, startDate: payloadStart, endDate: payloadEnd, daysBack: payloadDaysBack } = action.payload;
  const now = new Date();
  const defaultEnd = now.toISOString().slice(0, 10);
  const defaultDaysBack = 90;
  const defaultStart = new Date(now.getTime() - defaultDaysBack * 86400000).toISOString().slice(0, 10);

  const startDate = payloadStart?.slice(0, 10) ?? defaultStart;
  const endDate = payloadEnd?.slice(0, 10) ?? defaultEnd;

  const startMs = new Date(startDate).getTime();
  const endMs = new Date(endDate).getTime();
  const daysBack =
    payloadDaysBack ??
    (startMs && endMs ? Math.max(1, Math.ceil((endMs - startMs) / 86400000)) : defaultDaysBack);

  const params = {
    employeeId,
    startDate,
    endDate,
    daysBack,
    cancelToken: myPerformanceCancelSource.token,
  };

  for (const widget of WIDGET_KEYS) {
    yield fork(fetchWidget, placementService, widget, params);
  }
}

export function* fetchRecruiterOptions(action: PayloadAction<{ userId: number }>) {
  try {
    const userState: { userInfo?: { employeeId: number; firstName?: string; lastName?: string } } = yield select(
      selectUser,
    );
    const appUser = userState?.userInfo;
    const response = yield call(
      TrackRecruiters,
      getActiveRecruitersByLoggedInUserId,
      action.payload.userId,
      'R',
    );
    if (httpSuccess(response?.status) && appUser) {
      const recruiters = response?.data ?? [];
      const options = buildRecruiterOptions(recruiters, appUser);
      yield put(myPerformanceActions.setRecruiterOptions(options));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchRecruiterOptions',
        area: 'src/app/components/RecruiterWorkDesk/MyPerformanceDesk/MyPerformance.saga.ts',
      },
    });
  }
}

export function* myPerformanceSaga() {
  yield takeLatest(myPerformanceActions.fetchMyPerformanceDashboardAction.type, fetchMyPerformanceDashboard);
  yield takeLatest(myPerformanceActions.fetchRecruiterOptionsAction.type, fetchRecruiterOptions);
}
